import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ArtReproduction, ArtistProfileView } from '../types';
import { API_BASE } from '../services/api';
import ArtworkCard from '../components/gallery/ArtworkCard';
import ArtworkModal from '../components/gallery/ArtworkModal';
import GalleryFilters from '../components/gallery/GalleryFilters';
import { Tag, Size, ArtistOption } from '../types';
import './ArtistShop.css';

const ArtistShop: React.FC = () => {
  const { artistId } = useParams<{ artistId: string }>();
  const navigate = useNavigate();
  
  const [artistProfile, setArtistProfile] = useState<ArtistProfileView | null>(null);
  const [artworks, setArtworks] = useState<ArtReproduction[]>([]);
  const [filteredArtworks, setFilteredArtworks] = useState<ArtReproduction[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [availableSizes, setAvailableSizes] = useState<Size[]>([]);
  const [artists] = useState<ArtistOption[]>([]);
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedArtist] = useState<string>('');
  const [selectedArtwork, setSelectedArtwork] = useState<ArtReproduction | null>(null);
  const [selectedSizePrice, setSelectedSizePrice] = useState<{ size: string; price: number; isOriginal?: boolean } | undefined>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!artistId) {
        navigate('/art');
        return;
      }

      setLoading(true);
      try {
        const [profileRes, artworksRes, tagsRes, sizesRes] = await Promise.all([
          api.get<ArtistProfileView>(`/artists/${artistId}`).catch(() => ({ data: null })),
          api.get<ArtReproduction[]>(`/art`, { params: { artistId: parseInt(artistId) } }).catch(() => ({ data: [] })),
          api.get<Tag[]>('/tags').catch(() => ({ data: [] })),
          api.get<Size[]>('/sizes').catch(() => ({ data: [] }))
        ]);

        setArtistProfile(profileRes.data);
        // Use artworks from profile if available, otherwise use separate artworks call
        const profileArtworks = profileRes.data?.artworks || [];
        setArtworks(profileArtworks.length > 0 ? profileArtworks : (artworksRes.data || []));
        setTags(tagsRes.data || []);
        setAvailableSizes(sizesRes.data || []);
        
        // Debug: Log customization data
        if (profileRes.data) {
          console.log('Artist Profile Customization:', {
            heroImageUrl: profileRes.data.heroImageUrl,
            primaryColor: profileRes.data.primaryColor,
            storefrontLayout: profileRes.data.storefrontLayout,
            showSalesBanner: profileRes.data.showSalesBanner,
            showFeaturedSection: profileRes.data.showFeaturedSection,
            customMessage: profileRes.data.customMessage,
            activeSales: profileRes.data.activeSales
          });
        }
      } catch (error) {
        console.error('Failed to fetch artist shop data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [artistId, navigate]);

  // Apply filters
  useEffect(() => {
    let filtered = [...artworks];

    if (selectedTags.length > 0) {
      filtered = filtered.filter(artwork =>
        artwork.tags?.some(tag => selectedTags.includes(tag.id))
      );
    }

    if (selectedSize) {
      filtered = filtered.filter(artwork =>
        artwork.sizePrices?.some(sp => sp.size === selectedSize)
      );
    }

    setFilteredArtworks(filtered);
  }, [artworks, selectedTags, selectedSize]);

  const handleOpenArtwork = (artwork: ArtReproduction): void => {
    setSelectedArtwork(artwork);
    const firstSize = artwork.sizePrices?.[0];
    if (firstSize) {
      setSelectedSizePrice({
        size: firstSize.size,
        price: firstSize.price,
        isOriginal: firstSize.isOriginal
      });
    }
    setIsModalOpen(true);
  };

  const handleSizeSelect = (artworkId: number, sizePrice: { size: string; price: number; isOriginal?: boolean }): void => {
    setSelectedSizePrice(sizePrice);
  };

  const handleAddToCart = async (artwork: ArtReproduction, options?: { material?: string; frame?: string; hanging?: string; price?: number }): Promise<void> => {
    setIsSubmitting(true);
    try {
      let productId: number;
      const productsRes = await api.get<any[]>('/products', { params: { artReproductionId: artwork.id } });
      const products = productsRes.data ?? [];
      const existing = products.length === 1
        ? products[0]
        : products.find((p: any) => p.size === selectedSizePrice?.size) ?? products[0];

      if (existing) {
        productId = existing.id;
      } else {
        const size = selectedSizePrice?.size ?? 'Standard';
        const price = selectedSizePrice?.price ?? artwork.price ?? 0;
        const isOriginal = selectedSizePrice?.isOriginal ?? false;
        const productData = {
          name: `${artwork.title}${size !== 'Standard' ? ` - ${size}` : ''}`,
          description: artwork.description || `Art by ${artwork.artistName || 'Unknown Artist'}`,
          price,
          imageUrl: artwork.imageUrl,
          size,
          isCustomPrint: false,
          artReproductionId: artwork.id,
          isOriginal,
        };
        const createRes = await api.post<{ id: number }>('/products', productData);
        productId = createRes.data.id;
      }

      await api.post('/cart', {
        productId,
        quantity: 1
      });

      setIsModalOpen(false);
      // Show success toast (handled by toast context)
    } catch (error: any) {
      console.error('Failed to add to cart:', error);
      alert(error.response?.data?.message || 'Failed to add to cart');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTagToggle = (tagId: number): void => {
    setSelectedTags(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  const handleSizeChange = (value: string): void => {
    setSelectedSize(value);
  };

  const buildImageSrc = (imageUrl?: string): string | null => {
    if (!imageUrl) return null;
    return imageUrl.startsWith('http') ? imageUrl : `${API_BASE}${imageUrl}`;
  };

  if (loading) {
    return (
      <div className="artist-shop-page container">
        <div className="loading">Loading artist shop...</div>
      </div>
    );
  }

  if (!artistProfile) {
    return (
      <div className="artist-shop-page container">
        <div className="error">Artist not found</div>
      </div>
    );
  }

  // Build custom style object from profile customization
  const customStyles: React.CSSProperties & Record<string, string> = {};
  if (artistProfile.primaryColor) customStyles['--custom-primary'] = artistProfile.primaryColor;
  if (artistProfile.secondaryColor) customStyles['--custom-secondary'] = artistProfile.secondaryColor;
  if (artistProfile.accentColor) customStyles['--custom-accent'] = artistProfile.accentColor;
  if (artistProfile.backgroundColor) customStyles['--custom-bg'] = artistProfile.backgroundColor;
  if (artistProfile.textColor) customStyles['--custom-text'] = artistProfile.textColor;

  // Determine hero image - use custom hero if available, otherwise featured artwork
  const heroImageUrl = artistProfile.heroImageUrl 
    ? buildImageSrc(artistProfile.heroImageUrl)
    : (artistProfile.featuredArtworks?.[0]?.imageUrl 
      ? buildImageSrc(artistProfile.featuredArtworks[0].imageUrl)
      : null);

  return (
    <div 
      className={`artist-shop-page ${artistProfile.storefrontLayout || 'default'}`}
      style={customStyles}
    >
      <div className="container">
        {/* Sales Banner */}
        {artistProfile.showSalesBanner && artistProfile.activeSales && artistProfile.activeSales.length > 0 && (
          <div className="artist-sales-banner">
            {artistProfile.activeSales.map((sale) => (
              <div key={sale.id} className="sale-banner-item">
                <span className="sale-banner-text">
                  <strong>{sale.name}</strong>
                  {sale.description && <span> - {sale.description}</span>}
                </span>
                {sale.endDate && (
                  <span className="sale-banner-date">
                    Ends {new Date(sale.endDate).toLocaleDateString()}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="artist-shop-header">
          <button className="back-button" onClick={() => navigate('/art')}>
            <i className="fas fa-arrow-left"></i> Back to Shop
          </button>
          
          {/* Custom Hero Section */}
          {heroImageUrl && (
            <div 
              className="artist-shop-hero-banner"
              style={{ backgroundImage: `url(${heroImageUrl})` }}
            >
              <div className="artist-shop-hero-overlay">
                <div className="artist-shop-hero-content">
                  <h1>{artistProfile.displayName}</h1>
                  {artistProfile.customMessage && (
                    <p className="artist-custom-message">{artistProfile.customMessage}</p>
                  )}
                  {artistProfile.bio && !artistProfile.customMessage && (
                    <p className="artist-bio">{artistProfile.bio}</p>
                  )}
                  <div className="artist-social">
                    {artistProfile.instagram && (
                      <a href={artistProfile.instagram} target="_blank" rel="noopener noreferrer">
                        <i className="fab fa-instagram"></i>
                      </a>
                    )}
                    {artistProfile.website && (
                      <a href={artistProfile.website} target="_blank" rel="noopener noreferrer">
                        <i className="fas fa-globe"></i>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Default Hero (if no custom hero image) */}
          {!heroImageUrl && (
            <div className="artist-shop-hero">
              <div className="artist-shop-hero__image">
                <div className="artist-placeholder">{artistProfile.displayName.charAt(0)}</div>
              </div>
              <div className="artist-shop-hero__info">
                <h1>{artistProfile.displayName}</h1>
                {artistProfile.customMessage && (
                  <p className="artist-custom-message">{artistProfile.customMessage}</p>
                )}
                {artistProfile.bio && !artistProfile.customMessage && (
                  <p className="artist-bio">{artistProfile.bio}</p>
                )}
                <div className="artist-social">
                  {artistProfile.instagram && (
                    <a href={artistProfile.instagram} target="_blank" rel="noopener noreferrer">
                      <i className="fab fa-instagram"></i>
                    </a>
                  )}
                  {artistProfile.website && (
                    <a href={artistProfile.website} target="_blank" rel="noopener noreferrer">
                      <i className="fas fa-globe"></i>
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Featured Artworks Section */}
        {artistProfile.showFeaturedSection && artistProfile.featuredArtworks && artistProfile.featuredArtworks.length > 0 && (
          <div className="artist-featured-section">
            <h2>Featured Artworks</h2>
            <div className="featured-artworks-grid">
              {artistProfile.featuredArtworks.map((artwork) => (
                <ArtworkCard
                  key={artwork.id}
                  artwork={artwork}
                  onOpen={handleOpenArtwork}
                />
              ))}
            </div>
          </div>
        )}

        <div className="artist-shop-filters">
          <GalleryFilters
            tags={tags}
            availableSizes={availableSizes}
            artists={artists}
            selectedTags={selectedTags}
            selectedSize={selectedSize}
            selectedArtist={selectedArtist}
            onTagToggle={handleTagToggle}
            onSizeChange={handleSizeChange}
            onArtistChange={() => {}} // Not applicable in artist shop
          />
        </div>

        <div className="artist-shop-artworks">
          <h2>{artistProfile.showFeaturedSection && artistProfile.featuredArtworks && artistProfile.featuredArtworks.length > 0 ? 'All Artworks' : 'Artworks'}</h2>
          {filteredArtworks.length > 0 ? (
            <div className="artworks-grid">
              {filteredArtworks.map((artwork) => (
                <ArtworkCard
                  key={artwork.id}
                  artwork={artwork}
                  onOpen={handleOpenArtwork}
                />
              ))}
            </div>
          ) : (
            <div className="no-results">No artworks found</div>
          )}
        </div>

        <ArtworkModal
          isOpen={isModalOpen}
          artwork={selectedArtwork}
          selectedSize={selectedSizePrice}
          onSizeSelect={handleSizeSelect}
          onAddToCart={handleAddToCart}
          onClose={() => setIsModalOpen(false)}
          isSubmitting={isSubmitting}
        />
      </div>
    </div>
  );
};

export default ArtistShop;
